import { useEffect, useState } from 'react';
import { SUBSCRIPTION_STATUSES, type Subscription, type SubscriptionStatus } from '@provalia/shared';
import { fmtDate, fmtMoney } from '../../../shared/format';
import { Card, ErrorBox, useToast } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import { FormField } from '../../components/FormField';
import { SUBSCRIPTION_LABELS } from '../../labels';
import type { TenantTabProps } from '../TenantDetail';

interface Draft {
  status: SubscriptionStatus;
  monthlyFee: string;
  startDate: string;
  commitmentMonths: string;
  notes: string;
}

const STATUS_HINTS: Record<SubscriptionStatus, string> = {
  prueba: 'Periodo de prueba: el portal funciona con normalidad y no cuenta en el ingreso recurrente.',
  activo: 'Cliente de pago: cuenta en el ingreso mensual recurrente.',
  suspendido: 'Suspendido = el portal deja de estar disponible para el cliente y sus proveedores. El repaso de SAP se detiene.',
};

const toDraft = (s: Subscription): Draft => ({
  status: s.status,
  monthlyFee: s.monthlyFee === null ? '' : String(s.monthlyFee).replace('.', ','),
  startDate: s.startDate ?? '',
  commitmentMonths: s.commitmentMonths === null ? '' : String(s.commitmentMonths),
  notes: s.notes,
});

/** Fin de la permanencia: inicio + meses, sin pasar por la zona horaria. */
function commitmentEnd(start: string, months: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(start);
  if (!m) return null;
  const total = Number(m[1]) * 12 + Number(m[2]) - 1 + months;
  const y = Math.floor(total / 12);
  const mo = (total % 12) + 1;
  // 31 de enero + 1 mes = 28 (o 29) de febrero, no 3 de marzo.
  const day = Math.min(Number(m[3]), new Date(Date.UTC(y, mo, 0)).getUTCDate());
  return `${y}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** «1.234,50», «399,5» o «399.5» → número. Vacío → null. */
function parseAmount(text: string): number | null {
  const t = text.trim().replace(/\s|€/g, '');
  if (t === '') return null;
  return Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
}

export function SubscriptionCard({ slug, detail, reload }: TenantTabProps): JSX.Element {
  const toast = useToast();
  const sub = detail.tenant.subscription;
  const key = JSON.stringify(sub);
  const [draft, setDraft] = useState(() => toDraft(sub));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDraft(toDraft(JSON.parse(key) as Subscription)), [key]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]): void => setDraft((d) => ({ ...d, [k]: v }));
  const dirty = JSON.stringify(toDraft(sub)) !== JSON.stringify(draft);

  const fee = parseAmount(draft.monthlyFee);
  const months = draft.commitmentMonths.trim() === '' ? null : Number(draft.commitmentMonths);
  const feeError = fee !== null && (!Number.isFinite(fee) || fee < 0) ? 'Importe no válido' : null;
  const monthsError = months !== null && (!Number.isInteger(months) || months < 0 || months > 120) ? 'Entre 0 y 120 meses' : null;
  const end = draft.startDate && months ? commitmentEnd(draft.startDate, months) : null;

  const save = async (): Promise<void> => {
    if (feeError || monthsError) return;
    setBusy(true);
    setError(null);
    try {
      const body: Subscription = {
        status: draft.status,
        monthlyFee: fee,
        startDate: draft.startDate || null,
        commitmentMonths: months,
        notes: draft.notes,
      };
      await api.put(`${tenantPath(slug)}/subscription`, body);
      toast('Contrato guardado', 'ok');
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Suscripción y contrato">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid cols-4">
          <FormField label="Estado">
            <select className="select" value={draft.status} onChange={(e) => set('status', e.target.value as SubscriptionStatus)}>
              {SUBSCRIPTION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {SUBSCRIPTION_LABELS[s]}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Cuota mensual (€)" error={feeError} hint="Sin impuestos. Informativa: Provalia no factura.">
            <input className="input num" inputMode="decimal" placeholder="399" value={draft.monthlyFee} onChange={(e) => set('monthlyFee', e.target.value)} />
          </FormField>
          <FormField label="Fecha de inicio">
            <input className="input" type="date" value={draft.startDate} onChange={(e) => set('startDate', e.target.value)} />
          </FormField>
          <FormField
            label="Meses de permanencia"
            error={monthsError}
            hint={end ? `Hasta el ${fmtDate(end)}` : undefined}
          >
            <input className="input num" inputMode="numeric" placeholder="12" value={draft.commitmentMonths} onChange={(e) => set('commitmentMonths', e.target.value)} />
          </FormField>
        </div>
        <div className={`callout ${draft.status === 'suspendido' ? 'bad' : draft.status === 'activo' ? 'ok' : 'info'}`}>
          {STATUS_HINTS[draft.status]}
          {draft.status === 'activo' && fee ? <> Suma {fmtMoney(fee)} al mes.</> : null}
        </div>
        <FormField label="Notas" hint="Condiciones pactadas, contacto de facturación, lo que haga falta recordar.">
          <textarea className="textarea" value={draft.notes} maxLength={2000} onChange={(e) => set('notes', e.target.value)} />
        </FormField>
        <ErrorBox error={error} />
        <div className="row end">
          <button type="submit" className="btn primary" disabled={busy || !dirty || !!feeError || !!monthsError}>
            {busy ? 'Guardando…' : 'Guardar contrato'}
          </button>
        </div>
      </form>
    </Card>
  );
}
